import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ReactComponent as AlertSVG } from "go-core/images/svg/alert.svg";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import FormatResourceStatus from "../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { TerminalApi } from "../../../../../../../../services/Api/Admin/adminTypes";
import { AppVersionSearchSelectApi } from "../../../../../../../../services/Api/types";

const listName = "ADMIN_TERMINALS";
const resourceType = "TERMINAL";

interface ListState {
	resource?: Record<string, any>;
}

const List = ({ resource }: ListState) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const changeIdWithName = async (search?: string) => {
		const res = await adminApi.getListAppVersionsSearchSelect(search);
		return res.map((item) => {
			return {
				...item,
				id: item.label,
			};
		});
	};

	let config = {
		fields: [
			{
				id: "name",
				name: `${t("lib:common.word.name")}`,
				type: "text",
				render: (item: TerminalApi) => {
					return (
						<>
							<div className={"d-flex align-items-center"}>
								<span className={"me-1"}>
									{item.link ? (
										<a href={item.link} target={"_blank"} rel="noreferrer">
											{item.name}
										</a>
									) : (
										<>item.name</>
									)}{" "}
								</span>
								{item.master && <span className={"badge bg-primary me-1"}>Master</span>}{" "}
								<span className="badge bg-secondary">{item.type}</span>
								<br />
							</div>
							<small>
								{item.terminal_unique_id} ({item.terminal_id})
							</small>
						</>
					);
				},
				renderExport: (item: TerminalApi) =>
					`${item.name}(${item.type}) ${item.terminal_unique_id}(${item.terminal_id})`,
			},
			{
				id: "app_version",
				name: t("common.word.app_version"),
				render: (item: TerminalApi) => {
					return (
						<div className="d-flex align-items-center">
							<span>
								{item.app_version} {item.system_type}
							</span>
							{item.up_to_date === false && (
								<OverlayTrigger
									placement="top"
									overlay={
										<Tooltip id={`tooltip-${item.id}`}>
											{t("modules.terminal.field.not_up_to_date.title")}
										</Tooltip>
									}
								>
									{({ ref, ...triggerHandler }) => (
										<AlertSVG className={"ms-1"} ref={ref} {...triggerHandler} />
									)}
								</OverlayTrigger>
							)}
						</div>
					);
				},
				renderExport: (item: TerminalApi) => `${item.app_version} ${item.system_type}`,
			},
			{
				id: "organization",
				name: t("lib:common.word.location"),
				type: "search_select",
				render: (item: TerminalApi) => (
					<Link to={`/admin/organizations/${item.organization.id}`} target={"_blank"}>
						{item.organization.name}
					</Link>
				),
				renderExport: (item: TerminalApi) => item.organization.name,
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						adminApi.getListAdminOrganizationsListSearchSelect(search, params, undefined, {
							cancelToken: options?.token,
						}),
				},
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (item: TerminalApi) => `${FormatDate(item.created_at)}`,
			},
			{
				id: "details",
				name: t("common.word.details"),
				render: (item: TerminalApi) => {
					let str = "";
					if (item.ip_address) {
						str += `${t("modules.terminal.field.ip_address.title")} ${item.ip_address}\n`;
					}
					if (item.mac_address) {
						str += `${t("modules.terminal.field.mac_address.title")} ${item.mac_address}`;
					}
					return str;
				},
				renderExport: (item: TerminalApi) => {
					let str = "";
					if (item.ip_address) {
						str += `${t("modules.terminal.field.ip_address.title")} ${item.ip_address}\n`;
					}
					if (item.mac_address) {
						str += `${t("modules.terminal.field.mac_address.title")} ${item.mac_address}`;
					}
					return str;
				},
			},
			{
				id: "updated_at",
				name: t("lib:common.word.updated_at"),
				type: "date",
				render: (item: TerminalApi) => `${FormatDate(item.updated_at)}`,
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				type: "list",
				options: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DISABLED: t("enums.common.status.DISABLED", { ns: "lib" }),
					SUPPORTED: t("enums.common.status.SUPPORTED"),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
				render: (item: TerminalApi) => {
					return <FormatResourceStatus status={item.status} />;
				},
				renderExport: (item: TerminalApi) => `enums.common.status.${item.status}`,
			},
		],
		filters: [
			{
				id: "up_to_date",
				name: t("modules.terminal.field.up_to_date.title"),
				type: "boolean",
			},
			{
				id: "app_version",
				name: t("common.word.app_version"),
				type: "search_select",
				source: {
					request: async (search?: string) => changeIdWithName(search),
					render: (item: AppVersionSearchSelectApi) =>
						`${item.label} ${t(`enums.app_versions.status.${item.status}`)}`,
				},
			},
			{
				id: "app_version_greater_than",
				name: t("modules.terminal.field.app_version_greater_than.title"),
				type: "search_select",
				source: {
					request: async (search?: string) => changeIdWithName(search),
					render: (item: AppVersionSearchSelectApi) =>
						`${item.label} ${t(`enums.app_versions.status.${item.status}`)}`,
				},
			},
			{
				id: "app_version_less_than",
				name: t("modules.terminal.field.app_version_less_than.title"),
				type: "search_select",
				source: {
					request: async (search: string) => changeIdWithName(search),
					render: (item: AppVersionSearchSelectApi) =>
						`${item.label} ${t(`enums.app_versions.status.${item.status}`)}`,
				},
			},
			{
				id: "app_version_not",
				name: t("modules.terminal.field.app_version_not.title"),
				type: "search_select",
				source: {
					request: async (search?: string) => changeIdWithName(search),
					render: (item: AppVersionSearchSelectApi) =>
						`${item.label} ${t(`enums.app_versions.status.${item.status}`)}`,
				},
			},
		],
		actions: [],
		selectedColumns: ["name", "app_version", "organization", "created_at", "details", "updated_at", "status"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "organization";
			return adminApi.getAdminTerminals(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.terminal.header.title"),
			filename: t("modules.terminal.field.export_config.filename"),
		},
		numberOfStickyColumnsAtTheStart: 1,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};
	return <ListData config={config} />;
};

const AdminTerminalsIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation("admin");
	const isMobile = useWindowSize().isMobile;
	const segmentContextValue = React.useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.terminal.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.terminal.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};
export default AdminTerminalsIndexPage;
